import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LegalLayout } from './LegalLayout';
import { getLegalDoc, LEGAL_DOCS } from './legalDocs';
import { extractTocFromMarkdown, slugify } from './legalParse';
import { MobileSelect } from '../../components/ui/MobileSelect';
import { getLegalMarkdown } from './legalMarkdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Seo } from '../../components/seo/Seo';

type LoadState =
  | { status: 'idle' | 'loading' }
  | { status: 'loaded'; markdown: string }
  | { status: 'error'; message: string };

export default function LegalDocPage() {
  const { slug } = useParams();
  const doc = useMemo(() => getLegalDoc(slug), [slug]);
  const [state, setState] = useState<LoadState>({ status: 'idle' });

  const scrollToId = (id: string) => {
    if (!id) return;
    // Update hash for expected browser behavior / deep links.
    try {
      window.history.replaceState(null, '', `#${id}`);
    } catch {
      // ignore
    }

    const byId = document.getElementById(id);
    const target =
      byId ??
      (() => {
        try {
          // Some ids may include characters that benefit from escaping.
          return document.querySelector(`#${CSS.escape(id)}`);
        } catch {
          return null;
        }
      })();

    if (target && 'scrollIntoView' in target) {
      (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!doc) {
        setState({ status: 'error', message: 'Legal document not found.' });
        return;
      }

      setState({ status: 'loading' });
      try {
        const md = getLegalMarkdown(doc.slug);
        if (!md) throw new Error('Legal document content is missing.');
        if (!cancelled) setState({ status: 'loaded', markdown: md });
      } catch (e: any) {
        if (!cancelled) setState({ status: 'error', message: e?.message || 'Failed to load document.' });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  const toc = useMemo(() => {
    if (state.status !== 'loaded') return [];
    return extractTocFromMarkdown(state.markdown);
  }, [state]);

  const nodeToText = (node: any): string => {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(nodeToText).join('');
    // react-markdown can provide React elements; use their children if present.
    if (typeof node === 'object' && 'props' in node && node.props && 'children' in node.props) {
      return nodeToText(node.props.children);
    }
    return '';
  };

  return (
    <LegalLayout>
      <Seo
        title={`${doc?.title ?? 'Legal'} | Hustli`}
        description={`Read ${doc?.title ?? 'Hustli legal documents'} online.`}
        path={doc?.slug ? `/legal/${doc.slug}` : '/legal'}
        ogType="article"
      />
      <div className="mb-6">
        <nav className="text-sm text-stone-600 mb-4">
          <Link to="/" className="text-primary hover:underline">
            Home
          </Link>
          <span className="mx-2 text-stone-400">/</span>
          <Link to="/legal" className="text-primary hover:underline">
            Legal
          </Link>
          {doc && (
            <>
              <span className="mx-2 text-stone-400">/</span>
              <span className="text-stone-700">{doc.title}</span>
            </>
          )}
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-dark">{doc?.title ?? 'Legal'}</h1>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
        {state.status === 'loading' || state.status === 'idle' ? (
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary" />
              <div className="text-stone-700">Loading document…</div>
            </div>
          </div>
        ) : state.status === 'error' ? (
          <div className="p-6">
            <div className="text-red-700 font-semibold">Unable to load document</div>
            <div className="text-stone-700 mt-2">{state.message}</div>
            <div className="mt-4">
              <Link to="/legal" className="text-primary hover:underline font-medium">
                View all legal documents
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_280px]">
            {/* Document body */}
            <div className="p-6 sm:p-10">
              <div className="text-stone-800">
                {/* subtle “legal page” header line */}
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-stone-500 mb-8">
                  <span className="h-px bg-stone-200 flex-1" />
                  <span>HUSTLi LLC</span>
                  <span className="h-px bg-stone-200 flex-1" />
                </div>

                {/* Mobile TOC (top of document) */}
                <div className="lg:hidden mb-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-stone-600 mb-2">
                    Jump to section
                  </div>
                  <MobileSelect
                    className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary"
                    value=""
                    onChange={(id) => {
                      if (!id) return;
                      setTimeout(() => scrollToId(id), 0);
                    }}
                    options={[
                      { value: '', label: 'Select a section…' },
                      ...toc.map((t) => ({ value: t.id, label: t.text })),
                    ]}
                    aria-label="Jump to section"
                    sheetTitle="Jump to Section"
                  />
                </div>

                <div className="space-y-5 leading-relaxed">
                  {state.status === 'loaded' && (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h2: ({ children, ...props }) => {
                          const text = nodeToText(children).replace(/\s+/g, ' ').trim();
                          const id = slugify(text);
                          return (
                            <h2
                              id={id}
                              className="scroll-mt-28 pt-2 text-lg sm:text-xl font-bold text-dark tracking-tight"
                            >
                              {children as any}
                            </h2>
                          );
                        },
                        h1: ({ children, ...props }) => (
                          <h1 className="text-xl sm:text-2xl font-bold text-dark tracking-tight" {...props}>
                            {children}
                          </h1>
                        ),
                        h3: ({ children, ...props }) => (
                          <h3 className="text-base sm:text-lg font-bold text-dark tracking-tight" {...props}>
                            {children}
                          </h3>
                        ),
                        h4: ({ children, ...props }) => (
                          <h4 className="text-sm sm:text-base font-semibold text-dark tracking-tight" {...props}>
                            {children}
                          </h4>
                        ),
                        p: ({ children, ...props }) => (
                          <p className="text-sm sm:text-base text-stone-800" {...props}>
                            {children}
                          </p>
                        ),
                        ul: ({ children, ...props }) => (
                          <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-stone-800" {...props}>
                            {children}
                          </ul>
                        ),
                        ol: ({ children, ...props }) => (
                          <ol className="list-decimal pl-6 space-y-2 text-sm sm:text-base text-stone-800" {...props}>
                            {children}
                          </ol>
                        ),
                        li: ({ children, ...props }) => (
                          <li className="leading-relaxed" {...props}>
                            {children}
                          </li>
                        ),
                        a: ({ children, ...props }) => (
                          <a className="text-primary hover:underline" {...props}>
                            {children}
                          </a>
                        ),
                        hr: (props) => <hr className="border-stone-200 my-8" {...props} />,
                        strong: ({ children, ...props }) => (
                          <strong className="font-semibold text-stone-900" {...props}>
                            {children}
                          </strong>
                        ),
                      }}
                    >
                      {state.markdown}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </div>

            {/* TOC */}
            <aside className="border-t lg:border-t-0 lg:border-l border-stone-200 bg-stone-50/70 p-5 sm:p-6">
              <div className="lg:sticky lg:top-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-600">On this page</div>

                <div className="mt-4 hidden lg:block">
                  {toc.length > 0 ? (
                    <ul className="space-y-2">
                      {toc.slice(0, 40).map((t) => (
                        <li key={t.id}>
                          <a
                            href={`#${t.id}`}
                            className="block text-sm text-stone-700 hover:text-primary hover:underline"
                          >
                            {t.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-stone-600">No sections detected.</div>
                  )}
                </div>

                <div className="mt-6 pt-5 border-t border-stone-200">
                  <Link to="/legal" className="text-sm font-semibold text-primary hover:underline">
                    All legal documents
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-bold text-dark mb-3">Other documents</h2>
        <div className="flex flex-wrap gap-2">
          {LEGAL_DOCS.map((d) => (
            <Link
              key={d.slug}
              to={`/legal/${d.slug}`}
              className={`px-3 py-2 rounded-xl border text-sm ${
                d.slug === doc?.slug
                  ? 'border-primary bg-primary/5 text-primary font-semibold'
                  : 'border-stone-200 bg-white text-stone-700 hover:border-primary/60'
              }`}
            >
              {d.title}
            </Link>
          ))}
        </div>
      </div>
    </LegalLayout>
  );
}

